<template>
    <ValidationObserver ref="validationObserver" slim v-slot="{ valid, invalid }">
        <fieldset
            class="outgoings flex bg-white p-4 pt-8 rounded overflow-hidden relative viva-drop-shadow"
            :class="{ invalid: invalid }"
            @click="globalClick"
            :attributes="$attrs"
        >
            <div class="flex w-full flex-wrap">
                <div class="flex questions w-full flex-wrap" v-touch:swipe="swipeHandler">
                    <transition
                        tag="div"
                        name="question-slide"
                        mode="out-in"
                        duration="300"
                        @before-enter="beforeEnter"
                    >
                        <div
                            class="question summary w-full"
                            v-if="outgoings && currentIndex === 'summary'"
                            key="summary"
                        >
                            <div
                                class="flex w-full text-lg text-viva-money-black font-inter justify-center font-bold text-center"
                            >Your income &amp; expenditure summary</div>
                            <hr />
                            <div class="flex w-full flex-wrap overflow-hidden" v-if="outgoings">
                                <div class="flex w-full flex-wrap income-summary-container transition-all duration-1000 ease-in-out border-0 border-b border-solid border-gray-500 pb-2" :class="{ 'max-h-160': expandSummary, 'max-h-32': !expandSummary }">
                                    <div
                                        class="summary-row flex w-full py-2 flex-no-wrap"
                                        v-for="(question, index) in summaryQuestions"
                                        :key="index"
                                    >
                                        <div class="flex justify-start flex-1 text-viva-money-black font-inter" v-text="question.summary_label"></div>
                                        <div class="flex justify-end flex-grow-0">
                                            <div
                                                class="flex font-bold text-viva-money-black font-inter"
                                                v-text="formatForSummary(question.value || outgoings[question.name], false)"
                                            ></div>
                                            <div class="px-2"></div>
                                            <div
                                                v-if="question.name"
                                                class="flex text-sm underline cursor-pointer text-viva-money-red font-inter"
                                                @click="handleEdit(question.name)"
                                            >Edit</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="flex w-full flex-no-wrap mt-4">
                                <div class="flex flex-wrap p-2 w-1/3 justify-center">
                                    <div class="flex w-full text-center justify-center text-viva-money-black font-inter font-semibold">Income</div>
                                    <div
                                        class="text-xl font-bold text-viva-money-secondary-yellow font-inter"
                                    >{{ formatForSummary(totalIncomings) }}</div>
                                </div>
                                <div class="flex flex-wrap p-2 w-1/3 justify-center">
                                    <div class="flex w-full text-center justify-center text-viva-money-black font-inter font-semibold">Outgoings</div>
                                    <div
                                        class="text-xl font-bold text-viva-money-red font-inter"
                                    >{{ formatForSummary(totalOutgoings * -1) }}</div>
                                </div>
                                <div
                                    class="flex flex-wrap p-2 w-1/3 justify-center border border-solid border-transparent rounded"
                                    :class="{ 
                                        'border-status-red': iAndE >= 1, 
                                        'border-status-amber': iAndE >= 0.95 && iAndE < 1,
                                        'border-status-green': iAndE < 0.95,
                                    }"
                                >
                                    <div class="flex w-full text-center justify-center text-viva-money-black font-inter font-semibold">Money left</div>
                                    <div
                                        class="text-xl font-bold font-inter text-viva-money-secondary-yellow"
                                        :class="{ 
                                            'text-status-red': iAndE >= 1, 
                                            'text-status-green': iAndE < 0.95, 
                                            'text-status-amber': iAndE >= 0.95 && iAndE < 1,
                                            'border-status-red': iAndE >= 1, 
                                            'border-status-green': iAndE < 0.95, 
                                            'border-status-amber': iAndE >= 0.95 && iAndE < 1,
                                            }"
                                    >{{ formatForSummary(whatsLeft) }}</div>
                                </div>
                            </div>
                            <transition>
                                <div
                                    class="summary-message font-bold text-white p-4 mt-4 text-xs rounded bg-status-red font-inter"
                                    v-if="iAndE > 1"
                                >Warning: your outgoings are more than your income. This suggests you would be unable to repay any loan you may be offered. Please check your income and outgoings are correct before submitting your application. You may want to apply for a lower amount.</div>
                                <div
                                    class="summary-message font-bold text-white p-4 mt-4 text-xs rounded bg-status-amber font-inter"
                                    v-else-if="iAndE >= 0.95"
                                >You only have a small amount of money left - please make sure you can afford to pay back this loan before applying.</div>
                            </transition>
                        </div>
                        <div
                            v-else-if="currentIndex == 'loader'"
                            class="flex justify-center question w-full relative"
                        >
                        <div class="py-16">
                            <img
                            width="110"
                            height="110"
                            src="/img/tail-spin.svg?v=1"
                            />
                            </div>
                        </div>
                        <div
                            class="question w-full relative"
                            :key="'question-' + currentIndex"
                            v-else-if="currentQuestion"
                        >
                            <expand-transition :duration="150" origin="center">
                                <div class="absolute z-10 top-0 right-0 bottom-0 left-0" v-if="changing">
                                    <img
                                        src="/img/form/valid-field-4x.png"
                                        width="75"
                                        height="75"
                                        class="flex z-10 absolute question-tick"
                                    />
                                </div>
                            </expand-transition>
                            <fancy-select
                                :options="currentQuestion.options ? currentQuestion.options : {}"
                                :rules="$getValidationRules(name + '.' + currentIndex)"
                                :custom-messages="$getValidationMessages(name + '.' + currentIndex)"
                                :label="currentQuestion.label ? currentQuestion.label : ''"
                                :name="name + '.' + currentIndex"
                                :type="currentQuestion.type ? currentQuestion.type : 'normal'"
                                :tooltip="currentQuestion.tooltip || null"
                                v-model.number="outgoings[currentIndex]"
                                class="mb-8"
                                @input="handleInput"
                            ></fancy-select>
                        </div>
                    </transition>
                </div>
                <div
                    class="flex bottom-progress w-full flex-no-wrap flex-col"
                    v-if="currentIndex !== 'summary'"
                >
                    <div class="w-full progress-text my-2 text-xs leading-tight text-viva-money-black font-inter">
                        Please
                        <strong>only</strong> include your share of the monthly outgoings for the household.
                    </div>
                    <div class="outgoings-bar w-full rounded-lg bg-gray-600 overflow-hidden my-2">
                        <div class="inner bg-viva-money-secondary-yellow h-full" :style="{ width: progress + '%' }"></div>
                    </div>
                </div>
            </div>
        </fieldset>
    </ValidationObserver>
</template>

<script>
import FancySelect from "./../../components/form/fancyselect";
import { filter, map, reduce, isNumber, isObject, has, omit } from "underscore/modules/index";
import { get } from 'lodash';
import ExpandTransition from './../../components/expand-transition';
import Vue from 'vue';
import Vue2TouchEvents from 'vue2-touch-events';
 
Vue.use(Vue2TouchEvents);

export default {
    inject: [ '$getValidationRules', '$getValidationMessages' ],
    props: {
        "form": {
            type: Object
        },
        "customer": {
            default: () => ({})
        },
        "questions": {
            default: () => ({})
        },
        "name": String,
        "value": Object
    },
    created() {
        if (this.value && isObject(this.value)) {
            if (has(this.value, 'extra.currentIndex')) {
                this.currentIndex = get(this.value, 'extra.currentIndex');
            }
            if (has(this.value, 'extra')) {
                this.outgoings = omit(this.value, 'extra')
            }
        }
    },
    data() {
        return {
            currentIndex: "",
            outgoings: this.value ?? {},
            changing: false,
            expandSummary: true,
            isEditing: false,
        };
    },
    computed: {
        iAndE() {
            return this.totalOutgoings / this.totalIncomings;
        },
        whatsLeft() {
            if (
                isNumber(this.totalOutgoings) &&
                isNumber(this.totalIncomings)
            ) {
                return this.totalIncomings - this.totalOutgoings;
            } else {
                return null;
            }
        },
        totalOutgoings() {
            return (
                reduce(
                    this.outgoings,
                    function(memo, num, index) {
                        let ignore = ["has_loans", "has_credit_cards", "loans_taken_in_last_90_days"];
                        if (ignore.includes(index)) {
                            return memo;
                        }
                        if (isNumber(num)) {
                            if (memo === null) {
                                memo = 0;
                            }
                            return memo + num;
                        }
                    },
                    null
                ) + (this.customer.housing || 0)
            );
        },
        totalIncomings() {
            return this.customer.netpay;
        },
        progress() {
            let keys = Object.keys(this.questions);
            // swapped totalQuestions = to the amount of question keys to fix the progress calculation
            let totalQuestions = (Object.keys(this.questions)).length;
            let answeredQuestions = (function(keys, outgoings) {
                let tally = 0;
                keys.forEach(key => {
                    if (
                        typeof outgoings[key] !== "undefined" &&
                        outgoings[key] !== null
                    ) {
                        tally++;
                    }
                });
                return tally;
            })(keys, this.outgoings);
            return (answeredQuestions / totalQuestions) * 100;
        },
        currentQuestion() {
            return this.questions[this.currentIndex];
        },
        summaryQuestions() {
            // Add the key to the object
            let defaultOnes = [
                {
                    // name: 'housing',
                    summary_label: 'Mortgage/Rent',
                    value: this.customer.housing
                }
            ];
            let modded = map(this.questions, function(o, i) {
                o.name = i;
                return o;
            });
            // Only return those with summary label
            let filtered = filter(modded, (question) => {
                if (!this.outgoings.has_credit_cards && question.name === 'monthly_credit_card_repayments') {
                    return false;
                }
                if (!this.outgoings.has_loans && (question.name === 'monthly_loan_repayments' || question.name === 'loans_taken_in_last_90_days')) {
                    return false;
                }
                return typeof question.summary_label !== "undefined";
            });
            return defaultOnes.concat(filtered);
        }
    },
    methods: {
        swipeHandler(direction, event) {
            if (direction === 'right') {
                console.log('Go back');
            }
        },
        handleInput(val) {
            this.$emit("input", {...this.outgoings, ...{ extra: { currentIndex: this.currentIndex }}});
            // If this is in edit mode, then just chuck it straight back to the summary
            if (this.isEditing) {
                this.currentIndex = 'summary';
                return;
            }
            // Change to the next page
            let nextIndex = this.getNextPageIndex();
            if (nextIndex) {
                let delay = 500;
                if (nextIndex === "summary") {
                    this.currentIndex = "loader";
                    delay = 2500;
                }
                this.changing = true;
                setTimeout(() => {
                    this.currentIndex = nextIndex;
                    this.changing = false;
                }, delay);
            }
        },
        globalClick(event) {
            if (this.changing === true) {
                event.preventDefault();
            }
        },
        formatForSummary(value, withSign = true) {
            if (isNumber(value)) {
                let sign = value < 0 ? "-" : "+";
                return (withSign ? sign : "") + " £" + Math.abs(value);
            } else {
                return "--";
            }
        },
        handleEdit(name) {
            this.outgoings[name] = null;
            this.isEditing = true;
            this.currentIndex = name;
        },
        beforeEnter(el) {
            if (el && el.parentElement) {
                el.parentElement.style.height = el.offsetHeight + "px";
            }
        },
        getNextPageIndex() {
            // Loop through all of the questions
            let keys = Object.keys(this.questions);
            if (keys.indexOf(this.currentIndex) === keys.length - 1 && this.isEditing !== true) {
                return "summary";
            }
            for (
                let index = keys.indexOf(this.currentIndex);
                index < keys.length;
                index++
            ) {
                let questionName = keys[index];
                // let question = this.questions[questionName];
                // Check to see if we should skip this question
                if (
                    (questionName === "monthly_credit_card_repayments" &&
                        this.outgoings.has_credit_cards == 0) ||
                    (questionName === "monthly_loan_repayments" &&
                        this.outgoings.has_loans == 0) || 
                    (questionName === "loans_taken_in_last_90_days" &&
                        this.outgoings.has_loans == 0)
                ) {
                    continue;
                    // Check to see if it's already been answered
                } else if (
                    typeof this.outgoings[questionName] !== "undefined"
                ) {
                    continue;
                } else {
                    return questionName;
                }
            }
        }
    },
    watch: {
        outgoings: {
            deep: true,
            immediate: true,
            handler: function(val, oldVal) {
                let updated = {...val, ...{ extra: { currentIndex: this.currentIndex }}};
                this.$emit("input", updated);
                // Change to the next page
                let nextIndex = this.getNextPageIndex();
                if (nextIndex) {
                    let delay = 500;
                    if (nextIndex === "summary") {
                        this.currentIndex = "loader";
                        delay = 2500;
                    }
                    this.changing = true;
                    setTimeout(() => {
                        this.currentIndex = nextIndex;
                        this.changing = false;
                    }, delay);
                }
            }
        },
        'outgoings.has_credit_cards': {
            deep: true,
            handler: function(val) {
                if (val === 0) {
                    this.outgoings.monthly_credit_card_repayments = 0;
                }
            }
        },
        'outgoings.has_loans': {
            deep: true,
            handler: function(val) {
                if (val === 0) {
                    this.outgoings.monthly_loan_repayments = 0;
                }
            }
        },
        'outgoings.monthly_credit_card_repayments': {
            deep: true,
            handler: function(val) {
                if (val > 0) {
                    this.outgoings.has_credit_cards = 1;
                }
            }
        },
        'outgoings.monthly_loan_repayments': {
            deep: true,
            handler: function(val) {
                if (val > 0) {
                    this.outgoings.has_loans = 1;
                }
            }
        }
    },
    components: {
        "fancy-select": FancySelect,
        'expand-transition': ExpandTransition,
    },
    mounted() {
        if (this.outgoings.monthly_credit_card_repayments > 0) this.outgoings.has_credit_cards = 1;
        if (this.outgoings.monthly_loan_repayments > 0) this.outgoings.has_loans = 1;
        this.currentIndex = (this.customer.prepop === true) ? "summary" : Object.keys(this.questions)[0]; 
    }
};
</script>

<style lang="scss" scoped>
.outgoings {
    &.invalid {
        border: 1px solid theme('colors.status-red');
    }
}
.max-h-32 {
    max-height: 8rem;
}

.max-h-160 {
    max-height: 40rem;
}
.income-summary-button-container {
    background: linear-gradient(0deg, rgba(255,255,255,1) 50%, rgba(255,255,255,0) 100%);
}
.question-tick {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}
.outgoings-bar {
    height: 10px;
    .inner {
        transition: width 0.25s cubic-bezier(0.19, 1, 0.22, 1);
    }
}

.questions {
    min-height: 184px;
    transition: max-height 0.25s cubic-bezier(0.075, 0.82, 0.165, 1);
}

// Out states, either initial
.question-slide-enter {
    // position: absolute;
    opacity: 0;
    transform: translate(100%, 0);
    transform: translate3d(100%, 0, 0.0001%);
}
.question-slide-leave-to {
    // position: absolute;
    opacity: 0;
    transform: translate(-100%, 0);
    transform: translate3d(-100%, 0, 0.0001%);
}

// Not sure what these states are for
.question-slide-enter-to {
    transition: all 0.25s ease-out;
}
.question-slide-leave {
    box-sizing: border-box;
}

// Classes while the transition is happening
.question-slide-enter-active {
}
.question-slide-leave-active {
    transition: all 0.25s ease-out;
}
</style>